import React, { useCallback, useEffect, useState } from "react";
import { purchasesAPI, shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { formatMoney } from "../pos/utils/money";
import { formatDateTime } from "../utils/dates";
import DateRangeFilter, { rangeFor } from "../components/DateRangeFilter";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Every sale, and the one way to undo one.
 *
 * A refund reverses the whole sale — stock returns and the money leaves the
 * takings — so it asks first. Refunded sales stay in the list rather than
 * disappearing: the shop needs to see that a reversal happened.
 *
 * On a phone each sale is a card; the table only appears once there is room
 * for its six columns, instead of squeezing them until every cell wraps.
 */
const Purchases = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const [purchases, setPurchases] = useState([]);
  const [currency, setCurrency] = useState("TND");
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Which sales are open, and the items fetched for them. The list endpoint
  // carries no lines, so they are read on first open and kept.
  const [expanded, setExpanded] = useState({});
  const [items, setItems] = useState({});
  // Today first: "what happened today" is what the owner opens this to check,
  // and the whole history is one tap away rather than loaded every time
  const [range, setRange] = useState(() => rangeFor("today"));
  const [refreshing, setRefreshing] = useState(false);

  const fetchPurchases = useCallback(async () => {
    setRefreshing(true);
    try {
      // include_refunded so a reversal is visible here even though it no
      // longer counts towards revenue anywhere else
      const response = await purchasesAPI.getAll({
        include_refunded: true,
        start_date: range.start_date,
        end_date: range.end_date,
      });
      setPurchases(response.data);
      setError("");
    } catch (err) {
      setError(t("purchases.loadFailed"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start_date, range.end_date]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  useEffect(() => {
    shopsAPI
      .current()
      .then(({ data }) => data.shop?.currency && setCurrency(data.shop.currency))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refund = async (purchase) => {
    setBusy(true);
    setError("");

    try {
      await purchasesAPI.refund(purchase.id, reason || undefined);
      setConfirming(null);
      setReason("");
      await fetchPurchases();
    } catch (err) {
      setError(err.response?.data?.message || t("purchases.refundFailed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleDetails = async (purchase) => {
    const open = !expanded[purchase.id];
    setExpanded((current) => ({ ...current, [purchase.id]: open }));

    if (!open || items[purchase.id]?.lines) return;

    setItems((current) => ({ ...current, [purchase.id]: { loading: true } }));
    try {
      const { data } = await purchasesAPI.getById(purchase.id);
      setItems((current) => ({
        ...current,
        [purchase.id]: { lines: data.items || [] },
      }));
    } catch (err) {
      setItems((current) => ({
        ...current,
        [purchase.id]: { failed: true },
      }));
    }
  };

  const DetailsButton = ({ purchase }) => (
    <button
      type="button"
      onClick={() => toggleDetails(purchase)}
      aria-expanded={!!expanded[purchase.id]}
      className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 inline-flex items-center gap-1"
    >
      {expanded[purchase.id]
        ? t("purchases.hideDetails")
        : t("purchases.details")}
      <span aria-hidden="true">{expanded[purchase.id] ? "▴" : "▾"}</span>
    </button>
  );

  const ItemsList = ({ purchase }) => {
    const entry = items[purchase.id];

    if (!entry || entry.loading) {
      return <p className="text-sm text-gray-500">{t("purchases.itemsLoading")}</p>;
    }
    if (entry.failed) {
      return <p className="text-sm text-red-700">{t("purchases.itemsFailed")}</p>;
    }
    if (entry.lines.length === 0) {
      return <p className="text-sm text-gray-500">{t("purchases.noItems")}</p>;
    }

    return (
      <ul className="divide-y divide-gray-200 text-sm">
        {entry.lines.map((line) => (
          <li key={line.id} className="flex items-baseline gap-3 py-2">
            <span className="min-w-0 flex-1 text-gray-900">
              {line.product_name}
            </span>
            <span className="shrink-0 text-gray-500 tabular-nums" dir="ltr">
              {line.quantity} × {formatMoney(line.unit_price, currency)}
            </span>
            <span className="shrink-0 w-24 text-end font-semibold tabular-nums">
              {formatMoney(line.subtotal, currency)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  // A tab kept by name is who a credit sale was for, card or not
  const customerName = (purchase) =>
    purchase.user_name ||
    purchase.credit_account_name ||
    purchase.customer_label ||
    t("common.walkIn");

  const paymentLabel = (method) => {
    const key = `common.payment.${method}`;
    const label = t(key);
    return label === key ? method : label;
  };

  const SourcePill = ({ purchase }) =>
    purchase.preorder_id ? (
      <span className="inline-block whitespace-nowrap px-2.5 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
        {purchase.receipt_number
          ? t("purchases.source.preorderNumber", {
              number: purchase.receipt_number,
            })
          : t("purchases.source.preorder")}
      </span>
    ) : purchase.source === "pos" ? (
      <span className="inline-block whitespace-nowrap px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
        {purchase.receipt_number
          ? t("purchases.source.posNumber", { number: purchase.receipt_number })
          : t("purchases.source.pos")}
      </span>
    ) : (
      <span className="inline-block whitespace-nowrap px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
        {t("purchases.source.app")}
      </span>
    );

  const RefundControl = ({ purchase }) => {
    if (purchase.status === "completed" && !can("power.refund")) return null;

    if (purchase.status === "refunded") {
      return (
        <span className="inline-block whitespace-nowrap px-2.5 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          {t("purchases.refunded")}
        </span>
      );
    }

    if (confirming === purchase.id) {
      return (
        <div className="flex flex-col gap-2 w-full md:min-w-[220px]">
          <input
            type="text"
            autoFocus
            placeholder={t("purchases.reason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => refund(purchase)}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {busy ? "…" : t("purchases.confirmRefund")}
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              {t("common.keep")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={() => {
          setConfirming(purchase.id);
          setReason("");
        }}
        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
      >
        {t("purchases.refund")}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
          {t("purchases.title")}
        </h1>
        <p className="text-gray-600">{t("purchases.subtitle")}</p>
        <div className="mt-4">
          <DateRangeFilter value={range} onChange={setRange} />
        </div>
        <p
          className={`mt-3 text-sm text-gray-500 ${refreshing ? "opacity-60" : ""}`}
          aria-live="polite"
        >
          {t("purchases.count", { count: purchases.length })}
        </p>
      </div>

      {purchases.length === 0 ? (
        <EmptyState screen="purchases" message={range.preset === "all" ? t("purchases.empty") : t("purchases.emptyPeriod")} />
      ) : (
        <>
          {/* Phones: one card per sale */}
          <ul className="md:hidden space-y-3">
            {purchases.map((purchase) => (
              <li
                key={purchase.id}
                className={`bg-white rounded-xl shadow p-4 ${
                  purchase.status === "refunded" ? "opacity-75" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {customerName(purchase)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDateTime(purchase.created_at)}
                    </p>
                  </div>
                  <p
                    className={`shrink-0 text-lg font-bold tabular-nums ${
                      purchase.status === "refunded"
                        ? "text-gray-400 line-through"
                        : "text-gray-900"
                    }`}
                  >
                    {formatMoney(purchase.total_amount, currency)}
                  </p>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <SourcePill purchase={purchase} />
                  <span className="inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                    {paymentLabel(purchase.payment_method)}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-start gap-2">
                  <DetailsButton purchase={purchase} />
                  <RefundControl purchase={purchase} />
                </div>

                {expanded[purchase.id] && (
                  <div className="mt-3 border-t border-gray-100 pt-2">
                    <ItemsList purchase={purchase} />
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Tablet and up: the table */}
          <div className="hidden md:block bg-white rounded-xl shadow-lg p-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-start py-3 px-4">
                    {t("purchases.columns.date")}
                  </th>
                  <th className="text-start py-3 px-4">
                    {t("purchases.columns.source")}
                  </th>
                  <th className="text-start py-3 px-4">
                    {t("purchases.columns.customer")}
                  </th>
                  <th className="text-start py-3 px-4">
                    {t("purchases.columns.payment")}
                  </th>
                  <th className="text-end py-3 px-4">
                    {t("purchases.columns.amount")}
                  </th>
                  <th className="text-start py-3 px-4">
                    {t("purchases.columns.status")}
                  </th>
                  <th className="py-3 px-4" />
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <React.Fragment key={purchase.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-4 px-4 whitespace-nowrap">
                        {formatDateTime(purchase.created_at)}
                      </td>
                      <td className="py-4 px-4">
                        <SourcePill purchase={purchase} />
                      </td>
                      <td className="py-4 px-4">{customerName(purchase)}</td>
                      <td className="py-4 px-4">
                        {paymentLabel(purchase.payment_method)}
                      </td>
                      <td className="py-4 px-4 text-end font-bold whitespace-nowrap tabular-nums">
                        {formatMoney(purchase.total_amount, currency)}
                      </td>
                      <td className="py-4 px-4">
                        <RefundControl purchase={purchase} />
                      </td>
                      <td className="py-4 px-4">
                        <DetailsButton purchase={purchase} />
                      </td>
                    </tr>
                    {expanded[purchase.id] && (
                      <tr className="border-b bg-gray-50">
                        <td colSpan={7} className="px-6 py-3">
                          <ItemsList purchase={purchase} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Purchases;
